import {useMemo} from 'react'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {graphql, useClientQuery} from 'react-relay'

import type {usePreloadedLabelsQuery} from './__generated__/usePreloadedLabelsQuery.graphql'

/**
 * Dedicated GraphQL query for TSQ Label Picker preloaded data.
 *
 * This query is specifically designed for the TSQ (TanStack Query) Label Picker
 * to fetch label data that will be converted to preloaded format. Unlike the shared
 * LabelPickerClientQuery, this query uses inline fields instead of fragments,
 * allowing us to avoid fragment dependencies when passing data to the TSQ component.
 *
 * The TSQ Label Picker expects preloaded data to eliminate initial network requests
 * and provide immediate rendering of available labels, improving performance.
 *
 * This data is loaded into the Relay store in RelayRoute.tsx, where it reads data from
 * the `preloaded_records` key of the embedded data in the app.
 */
const usePreloadedLabelsQuery = graphql`
  query usePreloadedLabelsQuery($owner: String!, $repo: String!) {
    repository(owner: $owner, name: $repo) {
      id
      labels {
        # eslint-disable-next-line relay/unused-fields
        totalCount
        nodes {
          id
          # eslint-disable-next-line relay/unused-fields
          color
          # eslint-disable-next-line relay/unused-fields
          name
          # eslint-disable-next-line relay/unused-fields
          nameHTML
          # eslint-disable-next-line relay/unused-fields
          description
          # eslint-disable-next-line relay/unused-fields
          url
        }
      }
    }
  }
`

export function usePreloadedLabels(owner: string, repo: string) {
  const preloadedData = useClientQuery<usePreloadedLabelsQuery>(usePreloadedLabelsQuery, {
    owner,
    repo,
  })

  // Transform Relay GraphQL response into TSQ-compatible preloaded data format
  return useMemo(() => {
    if (!preloadedData?.repository?.labels?.nodes?.length || !preloadedData?.repository?.id) {
      return undefined
    }

    const labels = (preloadedData.repository.labels.nodes || []).flatMap(node => {
      if (!node) return []
      return [node]
    })

    return {
      repositoryId: preloadedData.repository.id,
      labels,
      totalCount: preloadedData.repository.labels.totalCount || 0,
    }
  }, [preloadedData])
}
